import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { QrCode, WifiHigh as Wifi, Link as LinkIcon, Lightning, Lock, Download, Pencil, Trash, CheckCircle, Copy, Sparkle, ArrowRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { id: 'link', label: 'Link diretto', icon: LinkIcon },
  { id: 'wifi', label: 'WiFi', icon: Wifi },
  { id: 'dynamic', label: 'Dinamico', icon: Lightning, badge: 'PRO' },
];

function buildWifiPayload({ ssid, security, password, hidden }) {
  // WiFi QR format: WIFI:T:<WPA|WEP|nopass>;S:<ssid>;P:<password>;H:<true|false>;;
  const esc = (s) => (s || '').replace(/([\\;,":])/g, '\\$1');
  const sec = security === 'nopass' ? 'nopass' : security; // WPA | WEP | nopass
  const parts = [`T:${sec}`, `S:${esc(ssid)}`];
  if (sec !== 'nopass') parts.push(`P:${esc(password)}`);
  if (hidden) parts.push('H:true');
  return `WIFI:${parts.join(';')};;`;
}

export default function QRCodePage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [access, setAccess] = useState(null);
  const [tab, setTab] = useState('link');
  const [link, setLink] = useState('https://contratti.vitadahost.it');
  const [wifi, setWifi] = useState({ ssid: '', security: 'WPA', password: '', hidden: false });
  const [dynamics, setDynamics] = useState([]);
  const [creatingDyn, setCreatingDyn] = useState({ name: '', target_url: '' });
  const [editing, setEditing] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const canvasRef = useRef(null);

  const fetchAccess = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/qrcode/access`, { withCredentials: true });
      setAccess(r.data);
      if (r.data.has_plugin) loadDynamics();
    } catch {}
  }, []);

  const loadDynamics = async () => {
    try {
      const r = await axios.get(`${API}/qrcode/dynamic`, { withCredentials: true });
      setDynamics(r.data || []);
    } catch {}
  };

  useEffect(() => { fetchAccess(); }, [fetchAccess]);

  // Verify purchase on return from Stripe
  useEffect(() => {
    const sid = params.get('session_id');
    const status = params.get('purchase');
    if (status === 'success' && sid) {
      axios.get(`${API}/qrcode/verify-purchase?session_id=${sid}`, { withCredentials: true })
        .then(r => {
          if (r.data.unlocked) {
            toast.success('🎉 Plugin QR Code sbloccato per sempre!', { duration: 5000 });
            fetchAccess();
          } else {
            toast.info('Pagamento in attesa...');
          }
          setParams({});
        })
        .catch(() => toast.error('Verifica pagamento fallita'));
    } else if (status === 'cancel') {
      toast.info('Pagamento annullato');
      setParams({});
    }
  }, [params, fetchAccess, setParams]);

  // Generate QR image whenever inputs change
  const payload = (() => {
    if (tab === 'link') return link;
    if (tab === 'wifi') return wifi.ssid ? buildWifiPayload(wifi) : '';
    return '';
  })();

  useEffect(() => {
    if (!payload || !canvasRef.current) { setQrUrl(''); return; }
    QRCode.toCanvas(canvasRef.current, payload, { width: 600, margin: 2, errorCorrectionLevel: 'H' }, (err) => {
      if (!err && canvasRef.current) setQrUrl(canvasRef.current.toDataURL('image/png'));
    });
  }, [payload]);

  const downloadQR = () => {
    if (!qrUrl) return;
    const a = document.createElement('a');
    a.href = qrUrl;
    a.download = `qrcode-${tab}-${Date.now()}.png`;
    a.click();
    toast.success('QR scaricato in alta risoluzione (1200px)');
  };

  const purchase = async () => {
    setPurchasing(true);
    try {
      const r = await axios.post(`${API}/qrcode/checkout`, { origin_url: window.location.origin }, { withCredentials: true });
      if (!r.data?.url) {
        toast.error('Stripe non ha restituito un link di pagamento');
        setPurchasing(false);
        return;
      }
      window.location.href = r.data.url;
    } catch (e) {
      // Espone il vero motivo (Stripe, server, network) invece di "Errore checkout"
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(d => d.msg).join(', ')
        : e.message || 'Errore checkout';
      toast.error(msg, { duration: 7000 });
      setPurchasing(false);
    }
  };

  const createDynamic = async () => {
    if (!creatingDyn.name || !creatingDyn.target_url) { toast.error('Nome e URL obbligatori'); return; }
    try {
      const r = await axios.post(`${API}/qrcode/dynamic`, creatingDyn, { withCredentials: true });
      setDynamics([r.data, ...dynamics]);
      setCreatingDyn({ name: '', target_url: '' });
      toast.success('QR dinamico creato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const updateDynamic = async (id, patch) => {
    try {
      await axios.patch(`${API}/qrcode/dynamic/${id}`, patch, { withCredentials: true });
      setDynamics(prev => prev.map(d => d.id === id ? { ...d, ...patch } : d));
      toast.success('QR aggiornato · stessa stampa, nuovo link');
      setEditing(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const deleteDynamic = async (id) => {
    if (!window.confirm('Eliminare il QR? Il QR stampato smetterà di funzionare.')) return;
    await axios.delete(`${API}/qrcode/dynamic/${id}`, { withCredentials: true });
    setDynamics(prev => prev.filter(d => d.id !== id));
    toast.success('Eliminato');
  };

  const downloadDynamicQR = async (d) => {
    const url = `${window.location.origin}/api/r/${d.short_id}`;
    const canvas = document.createElement('canvas');
    await QRCode.toCanvas(canvas, url, { width: 1200, margin: 2, errorCorrectionLevel: 'H' });
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `qr-dinamico-${d.short_id}.png`;
    a.click();
    toast.success('QR dinamico scaricato (1200px)');
  };

  // Paywall view
  if (access && !access.has_plugin) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <main className="max-w-3xl mx-auto px-4 py-10">
          <div className="flex items-center gap-2 mb-2">
            <QrCode size={20} weight="bold" className="text-violet-700" />
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-violet-700">PLUGIN Premium</p>
          </div>
          <h1 className="text-3xl font-black tracking-tight mb-3">Generatore QR Code</h1>
          <p className="text-zinc-500 mb-6 text-sm leading-relaxed max-w-xl">
            Genera QR Code in <strong>alta risoluzione</strong> (1200px) per link diretti, WiFi e — la killer feature — <strong>QR dinamici editabili</strong>.
          </p>

          <div className="bg-gradient-to-br from-violet-700 via-fuchsia-700 to-pink-700 text-white p-6 sm:p-8 mb-6 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-2.5 py-1 text-[10px] font-black uppercase tracking-wider mb-3">
                <Sparkle size={11} weight="fill" className="text-amber-300" /> Lifetime · Infiniti QR
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-5xl font-black tracking-tighter">€ 2,99</span>
                <span className="text-sm text-white/70 uppercase tracking-wider font-bold">una tantum</span>
              </div>
              <p className="text-sm text-white/90 mb-5">Paghi una sola volta. Crei QR illimitati. Per sempre.</p>
              <ul className="space-y-1.5 mb-5">
                {['QR link diretto · alta risoluzione 1200px', 'QR WiFi (WPA, WEP, aperta) · auto-connect ospiti', 'QR DINAMICO editabile · cambi il link senza ristampare', 'Download PNG illimitati · qualità per stampa', 'Nessun watermark · nessuna scadenza'].map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle size={14} weight="fill" className="text-amber-300 mt-0.5 flex-shrink-0" /> {b}</li>
                ))}
              </ul>
              <Button onClick={purchase} disabled={purchasing} className="rounded-none bg-white text-violet-700 hover:bg-violet-50 font-black uppercase tracking-wider px-8 py-6 text-sm" data-testid="qr-purchase-btn">
                {purchasing ? 'Apertura checkout...' : <>Sblocca subito <ArrowRight size={14} weight="bold" className="ml-1" /></>}
              </Button>
              <button
                onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/qrcode/self-unlock`, {}, { withCredentials: true });
                    if (r.data.unlocked) {
                      alert('Plugin QR sbloccato! Ricarico la pagina.');
                      window.location.reload();
                    } else {
                      alert(r.data.message || 'Nessun pagamento QR trovato per la tua email.');
                    }
                  } catch (err) {
                    alert(err?.response?.data?.detail || 'Errore durante lo sblocco. Riprova.');
                  }
                }}
                className="mt-3 block text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white underline underline-offset-4"
                data-testid="qr-self-unlock-btn"
              >
                Ho già pagato, sblocca ora
              </button>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
            <p className="text-xs text-amber-900 leading-relaxed"><strong>💡 Perché il QR dinamico è una killer feature:</strong> stampi un QR su una targa o flyer. Se il link cambia (cambi sito, sposti l'asset), basta editarlo dalla webapp — il QR già stampato continua a funzionare e rimanda al nuovo URL. Niente più ristampe!</p>
          </div>

          {/* Sezione spiegativa estesa — riprende contenuti home */}
          <section className="mt-8" data-testid="plugin-explainer">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-violet-700 mb-2">Cosa puoi fare</p>
            <h2 className="text-2xl font-black tracking-tight mb-5">3 modi per usare il plugin QR</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="border border-zinc-200 p-4 bg-white" data-testid="plugin-feature-static">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-violet-700 text-white flex items-center justify-center mb-3">
                  <QrCode size={18} weight="bold" />
                </div>
                <p className="text-sm font-black text-zinc-900 mb-1">QR Link Statico</p>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Genera un QR Code 1200px da qualsiasi URL. Menu digitali, biglietti, locandine, vetrina del b&amp;b.
                </p>
              </div>
              <div className="border border-zinc-200 p-4 bg-white" data-testid="plugin-feature-wifi">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-700 text-white flex items-center justify-center mb-3">
                  <Wifi size={18} weight="bold" />
                </div>
                <p className="text-sm font-black text-zinc-900 mb-1">QR WiFi auto-connect</p>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  WPA/WEP/aperta · gli ospiti scansionano e si collegano senza digitare la password. Stampa una volta, dura per sempre.
                </p>
              </div>
              <div className="border-2 border-violet-500 p-4 bg-violet-50 relative" data-testid="plugin-feature-dynamic">
                <span className="absolute -top-2 right-3 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-violet-600 text-white">Killer</span>
                <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-fuchsia-700 text-white flex items-center justify-center mb-3">
                  <Lightning size={18} weight="fill" />
                </div>
                <p className="text-sm font-black text-violet-900 mb-1">QR Dinamico editabile</p>
                <p className="text-xs text-violet-800 leading-relaxed">
                  Stampi UNA volta, cambi il link infinite volte. Il QR già stampato continua a funzionare — riemissioni e ristampe non servono più.
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-zinc-900 text-white p-4">
                <p className="text-[10px] font-black uppercase tracking-wider text-violet-300 mb-2">Casi d'uso reali</p>
                <ul className="space-y-1.5 text-xs text-zinc-200">
                  <li>· Welcome card in casa con QR alla guida ospite (PDF)</li>
                  <li>· Targa esterna con QR per check-in self-service</li>
                  <li>· Flyer locale con QR per recensione TripAdvisor</li>
                  <li>· QR WiFi sul frigo: gli ospiti si connettono in 2 secondi</li>
                  <li>· QR menu colazione che cambia ogni settimana — stampa una volta</li>
                </ul>
              </div>
              <div className="bg-white border border-zinc-200 p-4">
                <p className="text-[10px] font-black uppercase tracking-wider text-zinc-500 mb-2">Cosa include il plugin</p>
                <ul className="space-y-1.5 text-xs text-zinc-700">
                  <li className="inline-flex items-start gap-1.5"><CheckCircle size={11} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" /> Statici, WiFi e dinamici illimitati</li>
                  <li className="inline-flex items-start gap-1.5"><CheckCircle size={11} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" /> Download PNG 1200px qualità stampa</li>
                  <li className="inline-flex items-start gap-1.5"><CheckCircle size={11} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" /> Tracking scansioni per QR dinamici</li>
                  <li className="inline-flex items-start gap-1.5"><CheckCircle size={11} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" /> Nessun watermark · nessuna scadenza</li>
                  <li className="inline-flex items-start gap-1.5"><CheckCircle size={11} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" /> Pagamento una tantum · zero abbonamento</li>
                </ul>
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  }

  if (!access) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Caricamento...</div>;

  return (
    <div className="min-h-screen bg-zinc-50">
      <main className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        <div className="flex items-center gap-2 mb-1">
          <QrCode size={18} weight="bold" className="text-violet-700" />
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-violet-700">PLUGIN Sbloccato · Lifetime</p>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight mb-6">Generatore QR Code</h1>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-zinc-200">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all inline-flex items-center gap-2 ${tab === t.id ? 'border-violet-700 text-violet-700' : 'border-transparent text-zinc-500 hover:text-zinc-900'}`} data-testid={`qr-tab-${t.id}`}>
              <t.icon size={14} weight="bold" />
              {t.label}
              {t.badge && <span className="inline-flex items-center px-1.5 py-0.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-[8px] font-black tracking-wider">{t.badge}</span>}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: form */}
          <div className="lg:col-span-3 space-y-4">
            {tab === 'link' && (
              <div className="bg-white border border-zinc-200 p-5">
                <p className="text-[10px] font-black uppercase tracking-wider text-zinc-600 mb-3">URL di destinazione</p>
                <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://..." className="rounded-none" data-testid="qr-link-input" />
                <p className="text-[10px] text-zinc-400 mt-2">Il QR rimanderà sempre a questo URL. Una volta stampato non sarà più modificabile (usa il QR Dinamico se ti serve).</p>
              </div>
            )}

            {tab === 'wifi' && (
              <div className="bg-white border border-zinc-200 p-5 space-y-3">
                <div>
                  <Label className="text-[10px] font-black uppercase tracking-wider text-zinc-600">Nome rete (SSID)</Label>
                  <Input value={wifi.ssid} onChange={(e) => setWifi({ ...wifi, ssid: e.target.value })} placeholder="MioWiFi-Casa" className="rounded-none mt-1" data-testid="qr-wifi-ssid" />
                </div>
                <div>
                  <Label className="text-[10px] font-black uppercase tracking-wider text-zinc-600">Cifratura</Label>
                  <div className="grid grid-cols-3 gap-1 mt-1">
                    {[{ v: 'WPA', l: 'WPA/WPA2' }, { v: 'WEP', l: 'WEP' }, { v: 'nopass', l: 'Aperta' }].map(o => (
                      <button key={o.v} onClick={() => setWifi({ ...wifi, security: o.v })} className={`py-2 text-xs font-black uppercase tracking-wider border-2 ${wifi.security === o.v ? 'bg-violet-700 text-white border-violet-700' : 'bg-white text-zinc-500 border-zinc-300 hover:border-zinc-900'}`} data-testid={`qr-wifi-sec-${o.v}`}>{o.l}</button>
                    ))}
                  </div>
                </div>
                {wifi.security !== 'nopass' && (
                  <div>
                    <Label className="text-[10px] font-black uppercase tracking-wider text-zinc-600">Password</Label>
                    <Input type="text" value={wifi.password} onChange={(e) => setWifi({ ...wifi, password: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="qr-wifi-pass" placeholder="La tua password WiFi" />
                  </div>
                )}
                <label className="flex items-center gap-2 text-xs text-zinc-700">
                  <input type="checkbox" checked={wifi.hidden} onChange={(e) => setWifi({ ...wifi, hidden: e.target.checked })} className="rounded-none" /> Rete nascosta
                </label>
                <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-[11px] text-amber-900 leading-relaxed">
                  ⚠️ <strong>Importante:</strong> SSID e password devono essere scritti <strong>esattamente</strong> come nella configurazione del router: maiuscole/minuscole, spazi, trattini (-), underscore (_) inclusi. Anche un solo carattere sbagliato impedirà la connessione.
                </div>
              </div>
            )}

            {tab === 'dynamic' && (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border-2 border-violet-300 p-5">
                  <div className="flex items-start gap-2 mb-3">
                    <Lightning size={20} weight="fill" className="text-violet-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-black text-zinc-900 tracking-tight">QR Dinamico · La killer feature</p>
                      <p className="text-[11px] text-zinc-600 leading-relaxed mt-1">Crei il QR una volta, lo stampi, e <strong>cambi il link quando vuoi</strong> dalla webapp. La stampa resta valida per sempre.</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-zinc-200 p-5">
                  <p className="text-[10px] font-black uppercase tracking-wider text-zinc-600 mb-3">Crea nuovo QR dinamico</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input value={creatingDyn.name} onChange={(e) => setCreatingDyn({ ...creatingDyn, name: e.target.value })} placeholder="Nome (es. Targa entrata)" className="rounded-none" data-testid="qr-dyn-name" />
                    <Input value={creatingDyn.target_url} onChange={(e) => setCreatingDyn({ ...creatingDyn, target_url: e.target.value })} placeholder="URL iniziale (https://...)" className="rounded-none" data-testid="qr-dyn-url" />
                  </div>
                  <Button onClick={createDynamic} className="rounded-none mt-2 bg-violet-700 hover:bg-violet-800 text-white text-xs font-black uppercase tracking-wider" data-testid="qr-dyn-create">+ Crea QR dinamico</Button>
                </div>

                <div className="space-y-2" data-testid="qr-dyn-list">
                  {dynamics.length === 0 && <p className="text-xs text-zinc-400 text-center py-6">Nessun QR dinamico ancora · creane uno sopra</p>}
                  {dynamics.map(d => (
                    <div key={d.id} className="bg-white border border-zinc-200 p-4" data-testid={`qr-dyn-${d.id}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-black text-zinc-900 truncate">{d.name}</p>
                          <p className="text-[10px] font-mono text-zinc-400 truncate">/r/{d.short_id} · {d.scan_count} scan{d.scan_count === 1 ? '' : 's'}</p>
                          {editing === d.id ? (
                            <div className="flex gap-1 mt-2">
                              <Input defaultValue={d.target_url} onBlur={(e) => updateDynamic(d.id, { target_url: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && updateDynamic(d.id, { target_url: e.target.value })} className="rounded-none h-7 text-xs flex-1" autoFocus data-testid={`qr-dyn-edit-input-${d.id}`} />
                              <button onClick={() => setEditing(null)} className="text-[10px] font-bold uppercase text-zinc-500 px-2">✕</button>
                            </div>
                          ) : (
                            <p className="text-xs text-zinc-600 mt-1 truncate" title={d.target_url}>→ {d.target_url}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => downloadDynamicQR(d)} className="p-1.5 border border-zinc-300 hover:border-zinc-900" title="Scarica QR" data-testid={`qr-dyn-dl-${d.id}`}><Download size={12} weight="bold" /></button>
                          <button onClick={() => setEditing(editing === d.id ? null : d.id)} className="p-1.5 border border-zinc-300 hover:border-violet-700 hover:text-violet-700" title="Modifica URL" data-testid={`qr-dyn-edit-${d.id}`}><Pencil size={12} weight="bold" /></button>
                          <button onClick={() => deleteDynamic(d.id)} className="p-1.5 border border-zinc-300 hover:border-rose-600 hover:text-rose-600" title="Elimina"><Trash size={12} weight="bold" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: live preview */}
          {tab !== 'dynamic' && (
            <div className="lg:col-span-2">
              <div className="bg-white border border-zinc-200 p-5 sticky top-4">
                <p className="text-[10px] font-black uppercase tracking-wider text-zinc-600 mb-3">Anteprima · 1200px alta risoluzione</p>
                <div className="aspect-square bg-zinc-50 flex items-center justify-center border border-zinc-200 mb-3">
                  {payload ? <canvas ref={canvasRef} className="max-w-full max-h-full" /> : <p className="text-xs text-zinc-400 text-center px-4">Compila i campi a sinistra per vedere il QR</p>}
                </div>
                <Button onClick={downloadQR} disabled={!payload} className="w-full rounded-none bg-violet-700 hover:bg-violet-800 text-white text-xs font-black uppercase tracking-wider" data-testid="qr-download-btn">
                  <Download size={13} weight="bold" className="mr-1.5" /> Scarica PNG (1200px)
                </Button>
                <p className="text-[10px] text-zinc-400 mt-2 leading-relaxed text-center">Perfetto per stampa fino a 15×15 cm a 100 dpi.</p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

